/**
 * EventBus — two dispatch semantics:
 *
 *   emit(event, ...args)            – fire and forget. All listeners called.
 *   dispatch(event, ...args) → bool – interceptable chain. Listeners called in
 *                                     registration order; first to return true
 *                                     stops the chain.  Returns true if consumed.
 *
 * No singleton.  GameManager creates the instance and threads it everywhere.
 */

type EventCallback = (...args: any[]) => void | boolean | Promise<void> | Promise<boolean>;

export interface EventEmitter {
  emit(eventName: string, ...args: any[]): void;
  dispatch(eventName: string, ...args: any[]): boolean;
  dispatchAsync(eventName: string, payload: any): Promise<void>;
}

export default class EventBus {
  private listeners: Record<string, EventCallback[]> = {};

  on(eventName: string, callback: EventCallback): void {
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = [];
    }
    this.listeners[eventName].push(callback);
  }

  off(eventName: string, callback: EventCallback): void {
    if (this.listeners[eventName]) {
      this.listeners[eventName] = this.listeners[eventName].filter(
        listener => listener !== callback
      );
    }
  }

  emit(eventName: string, ...args: any[]): void {
    if (this.listeners[eventName]) {
      this.listeners[eventName].forEach(callback => {
        callback(...args);
      });
    }
  }

  /**
   * Interceptable dispatch.  Calls listeners in registration order.
   * Stops at the first listener that returns true (consumed).
   * @returns true if any listener consumed the event
   */
  dispatch(eventName: string, ...args: any[]): boolean {
    if (this.listeners[eventName]) {
      for (const callback of this.listeners[eventName]) {
        if (callback(...args) === true) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Dispatch an event with async completion callback.
   * Listeners receive { ...payload, resolve } and the first to call resolve() completes the promise.
   * If no listeners exist, resolves immediately (headless-first!).
   *
   * @param eventName - The event name
   * @param payload - Event data (resolve callback will be injected)
   * @returns Promise that resolves when a listener calls resolve(), or immediately if no listeners
   */
  async dispatchAsync(eventName: string, payload: any): Promise<void> {
    return new Promise((resolve) => {
      const hasListeners = this.listeners[eventName] && this.listeners[eventName].length > 0;

      if (hasListeners) {
        // Listeners exist - inject resolve callback for first one to call when done
        this.emit(eventName, { ...payload, resolve });
      } else {
        // Nobody listening - resolve immediately (headless mode!)
        resolve();
      }
    });
  }

  /**
   * Returns an object with only emit and dispatch methods.
   * Use this for passing to downstream classes that should only emit events, not subscribe.
   */
  getEmitter(): EventEmitter {
    return {
      emit: this.emit.bind(this),
      dispatch: this.dispatch.bind(this),
      dispatchAsync: this.dispatchAsync.bind(this)
    };
  }
}
